import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";
import dayjs from "dayjs";
import { Badge, Spinner } from "@radix-ui/themes";
import { buildParams, useExploreApi } from "@/v2/entities/explore/Explore.api";
import { QaRules, Sample } from "@/v2/entities/sampling/Sampling.api";
import {
  ExploreInteractionRow,
  ExploreInteractionType,
  exploreInteractionTypeToLabel,
} from "@/v2/entities/explore/Explore.model";
import { DATE_FORMAT } from "@/v2/assets/utils";
import { Comment01Icon } from "@/v2/assets/icons/Comment01Icon";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";

const sourceToIcon: Record<ExploreInteractionType, React.ReactNode> = {
  conversations: <Comment01Icon fill="orange" />,
  tickets: <Ticket03Icon fill="blue" />,
};

export interface ValidationRow {
  id: number;
  name: string;
  scorecardName: string;
  validationRules: QaRules;
  total: number;
  dateFrom: string;
  dateTo: string;
  sources: ExploreInteractionType[];

  // Explore data
  validated?: number | "loading";
  avgQaScore?: number | "loading";
  avgAutoQaScore?: number | "loading";
}

export function enhanceValidationRow(
  row: ValidationRow,
  interactions: ExploreInteractionRow[]
) {
  const validated = interactions.filter((x) => x.validationStatus).length;
  const filteredAvgQaScore: number[] = [];
  const filteredAvgAutoQaScore: number[] = [];

  interactions.forEach(({ avgQaScore, avgAutoQaScore }) => {
    if (avgQaScore != null && avgQaScore !== -1) {
      filteredAvgQaScore.push(avgQaScore);
    }
    if (avgAutoQaScore != null && avgAutoQaScore !== -1) {
      filteredAvgAutoQaScore.push(avgAutoQaScore);
    }
  });

  function calculateAverage(values: number[]) {
    return values.length > 0
      ? values.reduce((sum, x) => sum + x, 0) / values.length
      : undefined;
  }

  const avgQaScore = calculateAverage(filteredAvgQaScore);
  const avgAutoQaScore = calculateAverage(filteredAvgAutoQaScore);

  return { ...row, validated, avgQaScore, avgAutoQaScore };
}

export function ProgressFormatter({ total, validated }: ValidationRow) {
  if (validated == null) {
    return <></>;
  }

  if (validated === "loading") {
    return (
      <Badge>
        <Spinner size="1" />
        {` / ${total}`}
      </Badge>
    );
  }

  return <Badge>{`${validated ?? 0} / ${total}`}</Badge>;
}

export function TimeFrameFormatter({ dateFrom, dateTo }: ValidationRow) {
  const dateFromFormatted = dayjs(dateFrom).format(DATE_FORMAT);
  const dateToFormatted = dayjs(dateTo).format(DATE_FORMAT);

  return <>{`${dateFromFormatted} - ${dateToFormatted}`}</>;
}

export function SourcesFormatter({ sources }: ValidationRow) {
  return (
    <span className="inline-flex items-center gap-4">
      {sources.map((source) => (
        <span className="inline-flex items-center gap-1" key={source}>
          {sourceToIcon[source]}
          {exploreInteractionTypeToLabel[source].plural}
        </span>
      ))}
    </span>
  );
}

export function useSampleInteractions(sample: Sample) {
  const { getInteractions } = useExploreApi();

  const interactionIds = useMemo(
    () => [...(sample.tickets ?? []), ...(sample.conversations ?? [])],
    [sample.tickets, sample.conversations]
  );

  const initialParams = useMemo(() => {
    return buildParams({ filter: { id: { in: interactionIds } } });
  }, [interactionIds]);

  const query = useInfiniteQuery({
    queryKey: ["explore-v2", "sample-interactions", sample.id],
    queryFn: async ({ pageParam }) => {
      const params = (pageParam as string) ?? initialParams;
      const paramsString = params.length > 0 ? `?${params}` : "";
      return getInteractions(paramsString);
    },
    getNextPageParam: (lastPage) => lastPage?.next || undefined,
    enabled: interactionIds.length > 0,
  });

  const interactions = useMemo(
    () => query.data?.pages?.flatMap((page) => page.results) ?? [],
    [query.data]
  );

  return {
    interactions,
    isLoading: query.isLoading,
    hasNextPage: query.hasNextPage ?? false,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
}
